import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Paper,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Security,
  Campaign,
  Build,
  Delete,
  Warning,
  CheckCircle,
  Info,
} from '@mui/icons-material';
import { useCookieConsent } from '../contexts/CookieContext';
import SEOHead from '../components/SEOHead';

const CookiePreferencesPage = () => {
  const { preferences, savePreferences, resetConsent, getConsentStatus } = useCookieConsent();
  const [tempPreferences, setTempPreferences] = useState(preferences);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const consentStatus = getConsentStatus();

  const handlePreferenceChange = (cookieType) => (event) => {
    setTempPreferences({
      ...tempPreferences,
      [cookieType]: event.target.checked,
    });
  };

  const handleSavePreferences = () => {
    savePreferences(tempPreferences);
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  const handleResetAll = () => {
    resetConsent();
    setShowResetDialog(false);
    window.location.reload(); // Refresh to show banner again
  };

  const hasChanges = JSON.stringify(preferences) !== JSON.stringify(tempPreferences);

  const cookieTypes = [
    {
      key: 'necessary',
      title: 'Necessary Cookies',
      description: 'These cookies are essential for the website to function properly. They enable core functionality like security, network management, and accessibility.',
      details: [
        'Session management and authentication',
        'Security and CSRF protection',
        'Load balancing and performance',
        'Basic website functionality'
      ],
      icon: <Security />,
      required: true,
      color: 'primary',
    },
    {
      key: 'functional',
      title: 'Functional Cookies',
      description: 'These cookies enable enhanced functionality and personalization. They may be set by us or by third-party providers.',
      details: [
        'User preferences and settings',
        'Language and region selection',
        'Enhanced user interface features',
        'Personalized content delivery'
      ],
      icon: <Build />,
      required: false,
      color: 'secondary',
    },
    {
      key: 'marketing',
      title: 'Marketing Cookies',
      description: 'These cookies are used to track visitors across websites for advertising purposes and to provide more relevant advertisements.',
      details: [
        'Advertising targeting and measurement',
        'Social media integration',
        'Cross-site tracking prevention',
        'Personalized marketing content'
      ],
      icon: <Campaign />,
      required: false,
      color: 'warning',
    },
  ];

  const getConsentDate = () => {
    const consent = localStorage.getItem('pwgen-cookie-consent');
    if (consent) {
      const data = JSON.parse(consent);
      return new Date(data.timestamp).toLocaleDateString();
    }
    return null;
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <SEOHead
        title="Cookie Preferences"
        description="Manage your cookie preferences and privacy settings for PWGen-Rust website."
        canonicalUrl="https://pwgenrust.dev/cookie-preferences"
      />
      
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          component="h1"
          sx={{
            mb: 2,
            fontWeight: 700,
            textAlign: 'center',
            background: 'linear-gradient(45deg, #ff6b35, #4ecdc4)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Cookie Preferences
        </Typography>

        <Typography
          variant="h6"
          sx={{
            mb: 6,
            textAlign: 'center',
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          Manage your cookie preferences and control how we collect and use your data.
          You can enable or disable different types of cookies below.
        </Typography>

        {showSaveSuccess && (
          <Alert
            severity="success"
            icon={<CheckCircle />}
            sx={{ mb: 4 }}
          >
            Your cookie preferences have been saved successfully!
          </Alert>
        )}

        {/* Current Status */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info />
              Current Status
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body1">
                  Consent Status: {' '}
                  <Chip
                    label={consentStatus.consentGiven === null ? 'Not Set' : consentStatus.consentGiven ? 'Granted' : 'Declined'}
                    color={consentStatus.consentGiven === null ? 'default' : consentStatus.consentGiven ? 'success' : 'error'}
                    size="small"
                  />
                </Typography>
                {getConsentDate() && (
                  <Typography variant="body2" color="text.secondary">
                    Last updated: {getConsentDate()}
                  </Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setShowResetDialog(true)}
                sx={{ flexShrink: 0 }}
              >
                Reset All Cookies
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Cookie Categories */}
        <Stack spacing={3}>
          {cookieTypes.map((cookieType) => (
            <Card key={cookieType.key} sx={{ overflow: 'visible' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: `${cookieType.color}.main`,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 40,
                      height: 40,
                    }}
                  >
                    {cookieType.icon}
                  </Box>
                  
                  <Box sx={{ flexGrow: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {cookieType.title}
                        {cookieType.required && (
                          <Chip
                            label="Required"
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={tempPreferences[cookieType.key]}
                            onChange={handlePreferenceChange(cookieType.key)}
                            disabled={cookieType.required}
                            color={cookieType.color}
                          />
                        }
                        label=""
                      />
                    </Stack>
                    
                    <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                      {cookieType.description}
                    </Typography>
                    
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        What this includes:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {cookieType.details.map((detail, index) => (
                          <Typography
                            key={index}
                            component="li"
                            variant="body2"
                            sx={{ color: 'text.secondary', mb: 0.5 }}
                          >
                            {detail}
                          </Typography>
                        ))}
                      </Box>
                    </Paper>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {/* Action Buttons */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          sx={{ mt: 6 }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setTempPreferences(preferences)}
            disabled={!hasChanges}
            sx={{ minWidth: 150 }}
          >
            Reset Changes
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSavePreferences}
            disabled={!hasChanges}
            sx={{ minWidth: 150 }}
          >
            Save Preferences
          </Button>
        </Stack>

        {/* Additional Information */}
        <Card sx={{ mt: 6 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Additional Information
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              • Your preferences are stored locally in your browser and will persist across sessions.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              • You can change these settings at any time by returning to this page.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              • Clearing your browser data will reset these preferences.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              • For more information, please read our{' '}
              <a href="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>
                Privacy Policy
              </a>
              .
            </Typography>
          </CardContent>
        </Card>
      </Container>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Reset All Cookie Preferences
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to reset all cookie preferences? This will:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Clear all your current cookie preferences
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Show the cookie banner again on your next visit
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Reset all cookies to their default (declined) state
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleResetAll} color="error" variant="contained">
            Reset All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CookiePreferencesPage;